import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { EntityDialogData } from '../../services/entity-dialog.service';
import { EntityDefinition } from '../../types/entity-definition.interface';
import { EntityFormDeleteAction, EntityFormValue } from '../../types/entity-form-result.interface';
import { MeasureType } from '../../types/measure-type.interface';
import { GenericEntityFormComponent } from '../generic/generic-entity-form.component';

@Component({
  selector: 'app-measure-type-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    CheckboxModule,
    TextareaModule,
    GenericEntityFormComponent,
  ],
  templateUrl: './measure-type-form.component.html',
})
export class MeasureTypeFormComponent implements OnInit {
  @Input() entityDefinition?: EntityDefinition<MeasureType>;
  @Input() entity?: MeasureType;
  @Input() parentId?: number;
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() additionalData?: Record<string, unknown>;

  @Output() formSubmit = new EventEmitter<EntityFormValue<MeasureType>>();
  @Output() formCancel = new EventEmitter<void>();
  @Output() deleteEntity = new EventEmitter<MeasureType>();

  private readonly translateService = inject(TranslocoService);
  private readonly config = inject<DynamicDialogConfig<EntityDialogData<MeasureType>> | null>(DynamicDialogConfig, {
    optional: true,
  });
  private readonly dialogRef = inject<DynamicDialogRef<MeasureTypeFormComponent> | null>(DynamicDialogRef, {
    optional: true,
  });

  private directDeleteCallback?: (entity: MeasureType) => void;

  get isCreateMode(): boolean {
    return this.mode === 'create';
  }

  ngOnInit(): void {
    const data = this.config?.data;
    if (!data) {
      return;
    }
    this.entityDefinition = data.entityDefinition;
    this.mode = data.mode === 'edit' ? 'edit' : 'create';
    if (data.entity !== undefined) this.entity = data.entity;
    if (data.parentId !== undefined) this.parentId = data.parentId;
    if (data.additionalData !== undefined) this.additionalData = data.additionalData;
    if (data.onDelete !== undefined) this.directDeleteCallback = data.onDelete;
  }

  onFormSubmit(formData: EntityFormValue<MeasureType>): void {
    const description = formData['description'];
    if (typeof description === 'string' && description.length < 10 && this.dialogRef) {
      console.error(this.translateService.translate('common.errors.popupNotFound'));
      return;
    }

    if (this.dialogRef) {
      this.dialogRef.close(formData);
    } else {
      this.formSubmit.emit(formData);
    }
  }

  onFormCancel(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    } else {
      this.formCancel.emit();
    }
  }

  onDeleteRequest(entity: MeasureType): void {
    if (this.directDeleteCallback) {
      this.directDeleteCallback(entity);
      this.dialogRef?.close();
      return;
    }
    if (this.dialogRef) {
      const deleteAction: EntityFormDeleteAction<MeasureType> = { __action: 'delete', entity };
      this.dialogRef.close(deleteAction);
    } else {
      this.deleteEntity.emit(entity);
    }
  }
}
